/**
 * Loan Lifecycle Stage Enums
 *
 * Platform-standard loan origination stages. Unlike the per-business
 * configurable `CaseStatus` model (29-case-management-service), these
 * values are canonical across all tenants and drive:
 *  - The loan-detail stepper in the business portal
 *  - The simplified borrower stepper in the consumer portal
 *  - State-machine validation on `TransitionLoanStage` (gRPC)
 *  - Audit events (`LOAN_STAGE_CHANGED`)
 *
 * Industry reference: Mortgage Automator, Encompass (ICE Mortgage Tech),
 * nCino, Liquid Logics, Salesforce Financial Services Cloud.
 *
 * The two-level model (Stage + Substage) matches how mature LOS/LMS
 * platforms distinguish "where we are in the pipeline" (stage, ~8
 * stepper positions) from "what's blocking" (substage, granular
 * operational nuance). Parallel processes like appraisal ordering
 * belong in the Tasks tab, NOT as stages.
 *
 * Servicing-phase substages (CURRENT / DELINQUENT_*) are defined here
 * so the state machine can reach them, but dedicated UI (Servicing +
 * Draws tabs) is deferred to a separate service — see GH issue #1394.
 */

import { z } from 'zod';

// ============================================================================
// LOAN STAGE (primary — stepper positions)
// ============================================================================

/**
 * Canonical loan lifecycle stages. Drives the stepper UI.
 *
 * Terminal stages (DECLINED, WITHDRAWN, EXPIRED, CANCELLED, PAID_OFF,
 * CHARGED_OFF) render off-stepper — the stepper is greyed and a
 * terminal pill is shown next to it.
 *
 * @deprecated Use the per-business LoanStage row model (Issue #1388) — this
 * enum is kept for the consumer portal mapping during the dual-write window
 * only. New code should consume `trpc.loanStages.list/get` and read
 * `LoanStage.capabilities[]` via `LoanStageCapability` from
 * `@ops-matrix/shared-types`.
 */
export const LoanStage = {
  // Pipeline stages (visible on stepper)
  TO_REVIEW: 'TO_REVIEW',
  IN_PROGRESS: 'IN_PROGRESS',
  TERM_SHEET: 'TERM_SHEET',
  UNDERWRITING: 'UNDERWRITING',
  APPROVAL: 'APPROVAL',
  CLOSING: 'CLOSING',
  FUNDED: 'FUNDED',
  SERVICING: 'SERVICING',

  // Terminal stages (off-stepper)
  PAID_OFF: 'PAID_OFF',
  DECLINED: 'DECLINED',
  WITHDRAWN: 'WITHDRAWN',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
  CHARGED_OFF: 'CHARGED_OFF',
} as const;
export type LoanStage = (typeof LoanStage)[keyof typeof LoanStage];

export const LoanStageSchema = z.nativeEnum(LoanStage);

/**
 * Ordered stages shown on the business-portal stepper.
 *
 * @deprecated Use the per-business LoanStage row model (Issue #1388) — this
 * enum is kept for the consumer portal mapping during the dual-write window
 * only. The data-driven stepper reads stage rows from `trpc.loanStages.list`.
 */
export const LOAN_STEPPER_STAGES: LoanStage[] = [
  LoanStage.TO_REVIEW,
  LoanStage.IN_PROGRESS,
  LoanStage.TERM_SHEET,
  LoanStage.UNDERWRITING,
  LoanStage.APPROVAL,
  LoanStage.CLOSING,
  LoanStage.FUNDED,
  LoanStage.SERVICING,
];

/** Stages that render off-stepper with a terminal pill. */
export const TERMINAL_LOAN_STAGES: LoanStage[] = [
  LoanStage.PAID_OFF,
  LoanStage.DECLINED,
  LoanStage.WITHDRAWN,
  LoanStage.EXPIRED,
  LoanStage.CANCELLED,
  LoanStage.CHARGED_OFF,
];

const TERMINAL_LOAN_STAGES_SET: ReadonlySet<string> = new Set<string>(TERMINAL_LOAN_STAGES);

/**
 * @deprecated Use the per-business LoanStage row model (Issue #1388) — this
 * enum is kept for the consumer portal mapping during the dual-write window
 * only. New code should call `hasTerminalCapability(stage.capabilities)`
 * from `@ops-matrix/shared-types`.
 */
export function isTerminalLoanStage(stage: LoanStage | string | null | undefined): boolean {
  if (!stage) return false;
  return TERMINAL_LOAN_STAGES_SET.has(stage);
}

// ============================================================================
// LOAN SUBSTAGE (granular — shown as chip next to stage)
// ============================================================================

/**
 * Granular substages scoped to a primary stage. Optional on the Case
 * model — stage alone is always valid. The UI renders substage as a
 * small chip next to the active stage name.
 *
 * Grouped by parent stage for maintainability. When adding a new
 * substage, add the mapping to `SUBSTAGE_TO_STAGE` below so the
 * validator can reject substage/stage mismatches.
 *
 * @deprecated Use the per-business LoanSubstage row model (Issue #1388) —
 * this enum is kept for the consumer portal mapping during the dual-write
 * window only. New code should consume `LoanStage.substages[]` via
 * `trpc.loanStages.list({ includeSubstages: true })`.
 */
export const LoanSubstage = {
  // TO_REVIEW
  TO_REVIEW_NEW: 'TO_REVIEW_NEW',
  TO_REVIEW_ASSIGNED: 'TO_REVIEW_ASSIGNED',
  TO_REVIEW_ESCALATED: 'TO_REVIEW_ESCALATED',

  // IN_PROGRESS
  IN_PROGRESS_SIZING: 'IN_PROGRESS_SIZING',
  IN_PROGRESS_STRUCTURING: 'IN_PROGRESS_STRUCTURING',
  IN_PROGRESS_AWAITING_INFO: 'IN_PROGRESS_AWAITING_INFO',

  // TERM_SHEET
  TERM_SHEET_ISSUED: 'TERM_SHEET_ISSUED',
  TERM_SHEET_SIGNED: 'TERM_SHEET_SIGNED',
  TERM_SHEET_COUNTERED: 'TERM_SHEET_COUNTERED',

  // UNDERWRITING
  UNDERWRITING_PROCESSING: 'UNDERWRITING_PROCESSING',
  UNDERWRITING_IN_REVIEW: 'UNDERWRITING_IN_REVIEW',
  UNDERWRITING_AWAITING_APPRAISAL: 'UNDERWRITING_AWAITING_APPRAISAL',
  UNDERWRITING_AWAITING_TITLE: 'UNDERWRITING_AWAITING_TITLE',
  UNDERWRITING_CREDIT_MEMO_DRAFTED: 'UNDERWRITING_CREDIT_MEMO_DRAFTED',
  UNDERWRITING_SUSPENDED: 'UNDERWRITING_SUSPENDED',

  // APPROVAL
  APPROVAL_CONDITIONAL: 'APPROVAL_CONDITIONAL',
  APPROVAL_COMMITTEE_REVIEW: 'APPROVAL_COMMITTEE_REVIEW',
  APPROVAL_COMMITTED: 'APPROVAL_COMMITTED',

  // CLOSING
  CLOSING_CLEAR_TO_CLOSE: 'CLOSING_CLEAR_TO_CLOSE',
  CLOSING_DOCS_OUT: 'CLOSING_DOCS_OUT',
  CLOSING_DOCS_SIGNED: 'CLOSING_DOCS_SIGNED',
  CLOSING_FUNDING_REVIEW: 'CLOSING_FUNDING_REVIEW',

  // FUNDED
  FUNDED_INITIAL: 'FUNDED_INITIAL',
  FUNDED_POST_CLOSING_QC: 'FUNDED_POST_CLOSING_QC',

  // SERVICING (reachable but owned by deferred servicing service — issue #1394)
  SERVICING_CURRENT: 'SERVICING_CURRENT',
  SERVICING_GRACE: 'SERVICING_GRACE',
  SERVICING_DELINQUENT_30: 'SERVICING_DELINQUENT_30',
  SERVICING_DELINQUENT_60: 'SERVICING_DELINQUENT_60',
  SERVICING_DELINQUENT_90: 'SERVICING_DELINQUENT_90',
  SERVICING_DEFAULT: 'SERVICING_DEFAULT',
  SERVICING_MATURED: 'SERVICING_MATURED',
  SERVICING_EXTENSION_GRANTED: 'SERVICING_EXTENSION_GRANTED',
} as const;
export type LoanSubstage = (typeof LoanSubstage)[keyof typeof LoanSubstage];

export const LoanSubstageSchema = z.nativeEnum(LoanSubstage);

/** Parent stage for every substage — used to validate substage/stage pairs. */
export const SUBSTAGE_TO_STAGE: Record<LoanSubstage, LoanStage> = {
  [LoanSubstage.TO_REVIEW_NEW]: LoanStage.TO_REVIEW,
  [LoanSubstage.TO_REVIEW_ASSIGNED]: LoanStage.TO_REVIEW,
  [LoanSubstage.TO_REVIEW_ESCALATED]: LoanStage.TO_REVIEW,

  [LoanSubstage.IN_PROGRESS_SIZING]: LoanStage.IN_PROGRESS,
  [LoanSubstage.IN_PROGRESS_STRUCTURING]: LoanStage.IN_PROGRESS,
  [LoanSubstage.IN_PROGRESS_AWAITING_INFO]: LoanStage.IN_PROGRESS,

  [LoanSubstage.TERM_SHEET_ISSUED]: LoanStage.TERM_SHEET,
  [LoanSubstage.TERM_SHEET_SIGNED]: LoanStage.TERM_SHEET,
  [LoanSubstage.TERM_SHEET_COUNTERED]: LoanStage.TERM_SHEET,

  [LoanSubstage.UNDERWRITING_PROCESSING]: LoanStage.UNDERWRITING,
  [LoanSubstage.UNDERWRITING_IN_REVIEW]: LoanStage.UNDERWRITING,
  [LoanSubstage.UNDERWRITING_AWAITING_APPRAISAL]: LoanStage.UNDERWRITING,
  [LoanSubstage.UNDERWRITING_AWAITING_TITLE]: LoanStage.UNDERWRITING,
  [LoanSubstage.UNDERWRITING_CREDIT_MEMO_DRAFTED]: LoanStage.UNDERWRITING,
  [LoanSubstage.UNDERWRITING_SUSPENDED]: LoanStage.UNDERWRITING,

  [LoanSubstage.APPROVAL_CONDITIONAL]: LoanStage.APPROVAL,
  [LoanSubstage.APPROVAL_COMMITTEE_REVIEW]: LoanStage.APPROVAL,
  [LoanSubstage.APPROVAL_COMMITTED]: LoanStage.APPROVAL,

  [LoanSubstage.CLOSING_CLEAR_TO_CLOSE]: LoanStage.CLOSING,
  [LoanSubstage.CLOSING_DOCS_OUT]: LoanStage.CLOSING,
  [LoanSubstage.CLOSING_DOCS_SIGNED]: LoanStage.CLOSING,
  [LoanSubstage.CLOSING_FUNDING_REVIEW]: LoanStage.CLOSING,

  [LoanSubstage.FUNDED_INITIAL]: LoanStage.FUNDED,
  [LoanSubstage.FUNDED_POST_CLOSING_QC]: LoanStage.FUNDED,

  [LoanSubstage.SERVICING_CURRENT]: LoanStage.SERVICING,
  [LoanSubstage.SERVICING_GRACE]: LoanStage.SERVICING,
  [LoanSubstage.SERVICING_DELINQUENT_30]: LoanStage.SERVICING,
  [LoanSubstage.SERVICING_DELINQUENT_60]: LoanStage.SERVICING,
  [LoanSubstage.SERVICING_DELINQUENT_90]: LoanStage.SERVICING,
  [LoanSubstage.SERVICING_DEFAULT]: LoanStage.SERVICING,
  [LoanSubstage.SERVICING_MATURED]: LoanStage.SERVICING,
  [LoanSubstage.SERVICING_EXTENSION_GRANTED]: LoanStage.SERVICING,
};

/** Default substage to land on when entering a stage with no explicit substage. */
export const DEFAULT_SUBSTAGE_FOR_STAGE: Partial<Record<LoanStage, LoanSubstage>> = {
  [LoanStage.TO_REVIEW]: LoanSubstage.TO_REVIEW_NEW,
  [LoanStage.IN_PROGRESS]: LoanSubstage.IN_PROGRESS_SIZING,
  [LoanStage.TERM_SHEET]: LoanSubstage.TERM_SHEET_ISSUED,
  [LoanStage.UNDERWRITING]: LoanSubstage.UNDERWRITING_PROCESSING,
  [LoanStage.APPROVAL]: LoanSubstage.APPROVAL_CONDITIONAL,
  [LoanStage.CLOSING]: LoanSubstage.CLOSING_CLEAR_TO_CLOSE,
  [LoanStage.FUNDED]: LoanSubstage.FUNDED_INITIAL,
  [LoanStage.SERVICING]: LoanSubstage.SERVICING_CURRENT,
};

// ============================================================================
// TERMINAL REASON (ECOA adverse-action + pull-through reporting)
// ============================================================================

/**
 * Reason code attached when a loan enters a terminal stage. Required for
 * DECLINED (ECOA adverse-action notice) and informational for others.
 *
 * @deprecated Use the per-business LoanStage row model (Issue #1388) — the
 * reason code is now stored on the terminal `LoanStage.terminalReason`
 * column and varies per tenant. This enum is kept for the consumer portal
 * mapping during the dual-write window only.
 */
export const TerminalReason = {
  // Decline reasons
  DECLINED_CREDIT: 'DECLINED_CREDIT',
  DECLINED_COLLATERAL: 'DECLINED_COLLATERAL',
  DECLINED_INCOME_OR_DSCR: 'DECLINED_INCOME_OR_DSCR',
  DECLINED_POLICY: 'DECLINED_POLICY',
  DECLINED_INCOMPLETE: 'DECLINED_INCOMPLETE',
  DECLINED_OTHER: 'DECLINED_OTHER',

  // Withdrawal reasons (borrower-initiated)
  WITHDRAWN_BORROWER_CHOICE: 'WITHDRAWN_BORROWER_CHOICE',
  WITHDRAWN_LOST_TO_COMPETITOR: 'WITHDRAWN_LOST_TO_COMPETITOR',
  WITHDRAWN_DEAL_DIED: 'WITHDRAWN_DEAL_DIED',

  // Expired reasons (time-based, system)
  EXPIRED_TERM_SHEET: 'EXPIRED_TERM_SHEET',
  EXPIRED_COMMITMENT: 'EXPIRED_COMMITMENT',
  EXPIRED_RATE_LOCK: 'EXPIRED_RATE_LOCK',

  // Cancelled reasons (admin)
  CANCELLED_DUPLICATE: 'CANCELLED_DUPLICATE',
  CANCELLED_ADMIN: 'CANCELLED_ADMIN',

  // Paid-off reasons (exit paths)
  PAID_OFF_SALE: 'PAID_OFF_SALE',
  PAID_OFF_REFINANCE: 'PAID_OFF_REFINANCE',
  PAID_OFF_MATURITY: 'PAID_OFF_MATURITY',

  // Charge-off reasons
  CHARGED_OFF_DEFAULT: 'CHARGED_OFF_DEFAULT',
  CHARGED_OFF_SETTLEMENT: 'CHARGED_OFF_SETTLEMENT',
} as const;
export type TerminalReason = (typeof TerminalReason)[keyof typeof TerminalReason];

export const TerminalReasonSchema = z.nativeEnum(TerminalReason);

/** Terminal reasons valid for a given terminal stage. */
export const TERMINAL_REASONS_FOR_STAGE: Partial<Record<LoanStage, TerminalReason[]>> = {
  [LoanStage.DECLINED]: [
    TerminalReason.DECLINED_CREDIT,
    TerminalReason.DECLINED_COLLATERAL,
    TerminalReason.DECLINED_INCOME_OR_DSCR,
    TerminalReason.DECLINED_POLICY,
    TerminalReason.DECLINED_INCOMPLETE,
    TerminalReason.DECLINED_OTHER,
  ],
  [LoanStage.WITHDRAWN]: [
    TerminalReason.WITHDRAWN_BORROWER_CHOICE,
    TerminalReason.WITHDRAWN_LOST_TO_COMPETITOR,
    TerminalReason.WITHDRAWN_DEAL_DIED,
  ],
  [LoanStage.EXPIRED]: [
    TerminalReason.EXPIRED_TERM_SHEET,
    TerminalReason.EXPIRED_COMMITMENT,
    TerminalReason.EXPIRED_RATE_LOCK,
  ],
  [LoanStage.CANCELLED]: [TerminalReason.CANCELLED_DUPLICATE, TerminalReason.CANCELLED_ADMIN],
  [LoanStage.PAID_OFF]: [
    TerminalReason.PAID_OFF_SALE,
    TerminalReason.PAID_OFF_REFINANCE,
    TerminalReason.PAID_OFF_MATURITY,
  ],
  [LoanStage.CHARGED_OFF]: [TerminalReason.CHARGED_OFF_DEFAULT, TerminalReason.CHARGED_OFF_SETTLEMENT],
};

// ============================================================================
// STATE MACHINE (allowed stage transitions)
// ============================================================================

/**
 * Allowed destination stages from each stage. Terminal stages are
 * reachable from most pipeline stages (withdrawal/cancellation can
 * happen almost anywhere). SERVICING → terminal represents payoff or
 * charge-off; SERVICING self-loops are for substage changes handled
 * within the stage itself.
 *
 * @deprecated Use the per-business LoanStage row model (Issue #1388) — the
 * transition graph is now per-tenant, derived from the `LoanStage.order`
 * column plus capability-driven gates in
 * `loan-stage-requirements.checkExit`. This map is kept for the consumer
 * portal mapping during the dual-write window only.
 */
export const ALLOWED_STAGE_TRANSITIONS: Record<LoanStage, readonly LoanStage[]> = {
  [LoanStage.TO_REVIEW]: [LoanStage.IN_PROGRESS, LoanStage.DECLINED, LoanStage.WITHDRAWN, LoanStage.CANCELLED],
  [LoanStage.IN_PROGRESS]: [
    LoanStage.TERM_SHEET,
    LoanStage.UNDERWRITING, // allow skip for lenders that price in UW
    LoanStage.DECLINED,
    LoanStage.WITHDRAWN,
    LoanStage.CANCELLED,
  ],
  [LoanStage.TERM_SHEET]: [
    LoanStage.UNDERWRITING,
    LoanStage.IN_PROGRESS, // re-issue after counter-offer
    LoanStage.DECLINED,
    LoanStage.WITHDRAWN,
    LoanStage.EXPIRED,
    LoanStage.CANCELLED,
  ],
  [LoanStage.UNDERWRITING]: [
    LoanStage.APPROVAL,
    LoanStage.TERM_SHEET, // re-price after UW findings
    LoanStage.DECLINED,
    LoanStage.WITHDRAWN,
    LoanStage.CANCELLED,
  ],
  [LoanStage.APPROVAL]: [
    LoanStage.CLOSING,
    LoanStage.UNDERWRITING, // back to UW if conditions surface
    LoanStage.DECLINED,
    LoanStage.WITHDRAWN,
    LoanStage.EXPIRED, // commitment expired
    LoanStage.CANCELLED,
  ],
  [LoanStage.CLOSING]: [
    LoanStage.FUNDED,
    LoanStage.APPROVAL, // back-step for new conditions
    LoanStage.WITHDRAWN,
    LoanStage.EXPIRED, // rate-lock expired
    LoanStage.CANCELLED,
  ],
  [LoanStage.FUNDED]: [LoanStage.SERVICING, LoanStage.PAID_OFF],
  [LoanStage.SERVICING]: [LoanStage.PAID_OFF, LoanStage.CHARGED_OFF],

  // Terminal — generally no outbound transitions except admin "revive"
  [LoanStage.PAID_OFF]: [],
  [LoanStage.DECLINED]: [LoanStage.IN_PROGRESS, LoanStage.TO_REVIEW], // admin revive
  [LoanStage.WITHDRAWN]: [LoanStage.IN_PROGRESS, LoanStage.TO_REVIEW], // admin revive
  [LoanStage.EXPIRED]: [LoanStage.IN_PROGRESS, LoanStage.TERM_SHEET], // re-engagement
  [LoanStage.CANCELLED]: [LoanStage.IN_PROGRESS, LoanStage.TO_REVIEW], // admin revive
  [LoanStage.CHARGED_OFF]: [],
};

export interface CanTransitionResult {
  ok: boolean;
  reason?: string;
}

/**
 * Validates a stage transition. Returns `{ ok: true }` when allowed,
 * otherwise an error with a human-readable reason.
 *
 * Self-transitions (same stage) are allowed and represent substage
 * changes; the caller is responsible for validating the substage
 * belongs to the stage via `isSubstageValidForStage`.
 *
 * @deprecated Use the per-business LoanStage row model (Issue #1388) —
 * transitions are now validated by Service 29's
 * `loan-stage-requirements.checkExit` against the per-business stage
 * order + capability gates. This helper is kept for the consumer portal
 * mapping during the dual-write window only.
 */
export function canTransitionLoanStage(from: LoanStage, to: LoanStage): CanTransitionResult {
  if (from === to) return { ok: true };
  const allowed = ALLOWED_STAGE_TRANSITIONS[from];
  if (!allowed) return { ok: false, reason: `Unknown source stage "${from}"` };
  if (!allowed.includes(to)) {
    return {
      ok: false,
      reason: `Cannot transition from ${from} to ${to}. Allowed: ${allowed.join(', ') || '(none)'}`,
    };
  }
  return { ok: true };
}

/**
 * @deprecated Use the per-business LoanStage row model (Issue #1388) —
 * substage validity is now per-tenant via `LoanSubstage.loanStageId`. This
 * helper is kept for the consumer portal mapping during the dual-write
 * window only.
 */
export function isSubstageValidForStage(substage: LoanSubstage, stage: LoanStage): boolean {
  return SUBSTAGE_TO_STAGE[substage] === stage;
}

/**
 * @deprecated Use the per-business LoanStage row model (Issue #1388) —
 * terminal reasons are now per-tenant on the terminal `LoanStage` row's
 * `terminalReason` column. This helper is kept for the consumer portal
 * mapping during the dual-write window only.
 */
export function isTerminalReasonValidForStage(reason: TerminalReason, stage: LoanStage): boolean {
  return TERMINAL_REASONS_FOR_STAGE[stage]?.includes(reason) ?? false;
}

// ============================================================================
// CONSUMER PORTAL STAGE MAPPING (simplified 7 stages)
// ============================================================================

/**
 * Borrower-facing stage labels. Borrowers don't need to see credit-memo
 * drafts or committee scheduling; they want "where is my loan?".
 */
export const ConsumerLoanStage = {
  APPLIED: 'APPLIED',
  REVIEWING: 'REVIEWING',
  OFFER: 'OFFER',
  ACCEPTED: 'ACCEPTED',
  CLOSING: 'CLOSING',
  FUNDED: 'FUNDED',
  ACTIVE: 'ACTIVE',

  // Terminal (shown as alert card, not stepper position)
  TERMINAL: 'TERMINAL',
} as const;
export type ConsumerLoanStage = (typeof ConsumerLoanStage)[keyof typeof ConsumerLoanStage];

export const CONSUMER_STEPPER_STAGES: ConsumerLoanStage[] = [
  ConsumerLoanStage.APPLIED,
  ConsumerLoanStage.REVIEWING,
  ConsumerLoanStage.OFFER,
  ConsumerLoanStage.ACCEPTED,
  ConsumerLoanStage.CLOSING,
  ConsumerLoanStage.FUNDED,
  ConsumerLoanStage.ACTIVE,
];

/**
 * Maps the full LoanStage + (optional) LoanSubstage onto the
 * simplified consumer-facing stage. Substage affects the Term Sheet
 * stage only: SIGNED → ACCEPTED; ISSUED and COUNTERED both → OFFER
 * (counter-offer is still negotiation, not acceptance — the borrower
 * hasn't countersigned the lender's final terms).
 */
export function toConsumerLoanStage(
  stage: LoanStage | null | undefined,
  substage?: LoanSubstage | null | undefined,
): ConsumerLoanStage {
  if (!stage) return ConsumerLoanStage.APPLIED;
  if (isTerminalLoanStage(stage)) return ConsumerLoanStage.TERMINAL;

  switch (stage) {
    case LoanStage.TO_REVIEW:
    case LoanStage.IN_PROGRESS:
      return ConsumerLoanStage.APPLIED;
    case LoanStage.UNDERWRITING:
      return ConsumerLoanStage.REVIEWING;
    case LoanStage.TERM_SHEET:
      return substage === LoanSubstage.TERM_SHEET_SIGNED ? ConsumerLoanStage.ACCEPTED : ConsumerLoanStage.OFFER;
    case LoanStage.APPROVAL:
      return ConsumerLoanStage.ACCEPTED;
    case LoanStage.CLOSING:
      return ConsumerLoanStage.CLOSING;
    case LoanStage.FUNDED:
      return ConsumerLoanStage.FUNDED;
    case LoanStage.SERVICING:
      return ConsumerLoanStage.ACTIVE;
    default:
      return ConsumerLoanStage.APPLIED;
  }
}

// ============================================================================
// I18N HELPERS
// ============================================================================

/**
 * Normalize an arbitrary stage / substage / reason name into an i18n-key suffix.
 * Lowercases and converts whitespace + dashes to underscores so both legacy
 * SCREAMING_SNAKE enums (`TO_REVIEW`) and the per-business display names
 * (`"To Review"`) resolve to the same key (`to_review`). Issue #1388.
 */
function toI18nKeySuffix(value: string | null | undefined): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

/** i18n key for a loan stage label (business portal). */
export function getLoanStageLabelKey(stage: LoanStage | string): string {
  return `loan.stage.${toI18nKeySuffix(stage)}`;
}

/** i18n key for a loan substage label (business portal). */
export function getLoanSubstageLabelKey(substage: LoanSubstage | string): string {
  return `loan.substage.${toI18nKeySuffix(substage)}`;
}

/** i18n key for a terminal reason label. */
export function getTerminalReasonLabelKey(reason: TerminalReason | string): string {
  return `loan.terminalReason.${toI18nKeySuffix(reason)}`;
}

/** i18n key for a consumer-facing stage label. */
export function getConsumerLoanStageLabelKey(stage: ConsumerLoanStage | string): string {
  return `loan.consumerStage.${toI18nKeySuffix(stage)}`;
}

/**
 * i18n key for the "what to do next" copy for a loan stage. Returns `null`
 * for terminal stages — callers shouldn't render an action line for them.
 */
export function getLoanStageActionKey(stage: LoanStage | string | null | undefined): string | null {
  if (!stage || isTerminalLoanStage(stage)) return null;
  return `loan.action.${toI18nKeySuffix(stage)}`;
}

// ============================================================================
// STAGE → TAB RELEVANCE (for "connected to fields/tabs" UI)
// ============================================================================

/**
 * Tabs where the user is most likely to work while the loan is at a given
 * stage. The business-portal loan-detail UI uses this to accent the
 * relevant tab — a dot or underline — so the user can see at a glance
 * where to focus without clicking through every tab.
 *
 * Tab IDs match `CASE_TABS` in the loan detail page. Order matters: the
 * first entry is the "primary" tab (accented more strongly); subsequent
 * entries are "secondary" (dot only).
 */
export const LOAN_STAGE_TO_PRIMARY_TABS: Partial<Record<LoanStage, readonly string[]>> = {
  [LoanStage.TO_REVIEW]: ['overview', 'application'],
  [LoanStage.IN_PROGRESS]: ['application', 'parties', 'dataRoom'],
  [LoanStage.TERM_SHEET]: ['dataRoom', 'parties'],
  [LoanStage.UNDERWRITING]: ['underwriting', 'tasks', 'dataRoom', 'notes'],
  [LoanStage.APPROVAL]: ['underwriting', 'activity', 'notes'],
  [LoanStage.CLOSING]: ['closing', 'dataRoom', 'parties'],
  [LoanStage.FUNDED]: ['closing', 'activity'],
  [LoanStage.SERVICING]: ['activity', 'messages'],
};

export function getPrimaryTabsForStage(stage: LoanStage | string | null | undefined): readonly string[] {
  if (!stage) return [];
  return LOAN_STAGE_TO_PRIMARY_TABS[stage as LoanStage] ?? [];
}

export function isTabPrimaryForStage(tabId: string, stage: LoanStage | string | null | undefined): boolean {
  const tabs = getPrimaryTabsForStage(stage);
  return tabs[0] === tabId;
}

export function isTabSecondaryForStage(tabId: string, stage: LoanStage | string | null | undefined): boolean {
  const tabs = getPrimaryTabsForStage(stage);
  return tabs.slice(1).includes(tabId);
}
